import { useState } from 'react';

interface ProductGalleryProps {
  images: string[];
  productName: string;
  badge?: string;
}

const ProductGallery = ({ images, productName, badge }: ProductGalleryProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);

  const handlePrev = () => setActiveIndex(i => (i === 0 ? images.length - 1 : i - 1));
  const handleNext = () => setActiveIndex(i => (i === images.length - 1 ? 0 : i + 1));

  return (
    <div className="flex flex-col gap-4">
      {/* Main image */}
      <div
        className="relative bg-gradient-to-b from-[#161616] to-[#0d0d0d] rounded-2xl overflow-hidden border border-white/8 cursor-zoom-in"
        style={{ minHeight: '520px' }}
        onClick={() => setZoomed(true)}
      >
        {badge && (
          <div className="absolute top-5 left-5 z-10 bg-[#c9a84c] text-black text-[10px] tracking-widest uppercase font-bold px-3 py-1.5 rounded-full whitespace-nowrap">
            {badge}
          </div>
        )}
        <div className="absolute top-5 right-5 z-10 w-8 h-8 flex items-center justify-center bg-black/40 rounded-full text-white/50 hover:text-white transition-colors">
          <i className="ri-zoom-in-line text-sm"></i>
        </div>

        <img
          src={images[activeIndex]}
          alt={`${productName} — foto ${activeIndex + 1}`}
          className="w-full h-full object-contain object-top transition-opacity duration-300"
          style={{ minHeight: '520px', maxHeight: '520px' }}
        />

        {/* Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={e => { e.stopPropagation(); handlePrev(); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/50 hover:bg-[#c9a84c]/20 border border-white/10 hover:border-[#c9a84c]/40 rounded-full text-white/60 hover:text-[#c9a84c] transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <button
              onClick={e => { e.stopPropagation(); handleNext(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/50 hover:bg-[#c9a84c]/20 border border-white/10 hover:border-[#c9a84c]/40 rounded-full text-white/60 hover:text-[#c9a84c] transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </>
        )}

        {/* Dots */}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={e => { e.stopPropagation(); setActiveIndex(i); }}
                className={`rounded-full transition-all cursor-pointer ${i === activeIndex ? 'w-5 h-1.5 bg-[#c9a84c]' : 'w-1.5 h-1.5 bg-white/30 hover:bg-white/60'}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                i === activeIndex
                  ? 'border-[#c9a84c] opacity-100'
                  : 'border-white/10 opacity-50 hover:opacity-80 hover:border-white/30'
              }`}
            >
              <img
                src={img}
                alt={`${productName} thumbnail ${i + 1}`}
                className="w-full h-full object-cover object-top"
              />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {zoomed && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-6"
          onClick={() => setZoomed(false)}
        >
          <button
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center text-white/60 hover:text-white transition-colors cursor-pointer"
            onClick={() => setZoomed(false)}
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          {images.length > 1 && (
            <>
              <button
                onClick={e => { e.stopPropagation(); handlePrev(); }}
                className="absolute left-5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-[#c9a84c]/20 rounded-full text-white/70 hover:text-[#c9a84c] transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-xl"></i>
              </button>
              <button
                onClick={e => { e.stopPropagation(); handleNext(); }}
                className="absolute right-5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-[#c9a84c]/20 rounded-full text-white/70 hover:text-[#c9a84c] transition-all cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-xl"></i>
              </button>
            </>
          )}
          <img
            src={images[activeIndex]}
            alt={productName}
            className="max-h-[85vh] max-w-[85vw] object-contain"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
